import React, { Fragment } from 'react';
import { capitalizeFirstLetter } from '~domain/helper/formatters';
import DateTime from '~domain/model/core/date/datetime';
import { PolicySummaryFacade } from '~domain/model/facade/policy/policy-summary-facade';
import { PolicyAttachmentSummary } from '~domain/model/policy/policy-attachment-summary';
import {
  getPolicyAttachmentCategory,
  PolicyAttachmentCategory,
} from '~frontend-common/components/policy-attachments-list/policy-attachment-categories';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box';
import { Divider } from '~ui-kit/components/divider';
import { Typography } from '~ui-kit/components/typography';
import { theme } from '~ui-kit/theme';
import { PolicyAttachmentCard } from './policy-attachment-card';

interface PolicyAttachmentsListProps {
  policy: PolicySummaryFacade;
  showDebug?: boolean;
  showNotificationIcon?: boolean;
}

type MonthAttachments = {
  month: DateTime;
  attachments: PolicyAttachmentSummary[];
};

export function PolicyAttachmentsList({ policy, showDebug, showNotificationIcon = false }: PolicyAttachmentsListProps) {
  const translator = useTranslator();

  /**
   * Organize the attachments by year/month and sort everything to show the most recent first
   */
  const groupedByMonth: MonthAttachments[] = policy.attachments
    .filter((x) => showDebug || getPolicyAttachmentCategory(x.type) !== PolicyAttachmentCategory.Debug)
    .reduce((acc, attachment) => {
      const monthStart = new DateTime(attachment.receivedDate);

      // Check if the month already exists in the accumulator
      let monthGroup = acc.find((group) => group.month.isSameMonth(monthStart));

      if (!monthGroup) {
        // If not found, create a new group
        monthGroup = { month: monthStart, attachments: [] };
        acc.push(monthGroup);
      }

      // Add the attachment to the corresponding month's group
      monthGroup.attachments.push(attachment);

      return acc;
    }, [] as MonthAttachments[])
    // Sort months by the most recent first
    .sort((a, b) => b.month.Moment.toDate().getTime() - a.month.Moment.toDate().getTime())
    // Sort documents within each month by the most recent first
    .map((monthGroup) => ({
      ...monthGroup,
      documents: monthGroup.attachments.sort(
        (a, b) =>
          new DateTime(b.receivedDate).Moment.toDate().getTime() -
          new DateTime(a.receivedDate).Moment.toDate().getTime(),
      ),
    }));

  return (
    <Box flexDirection="column" mt={5} gridGap={5}>
      {groupedByMonth.length === 0 && (
        <Typography $color={theme.colors.gray}>{translator.t('policies.documents.empty')}</Typography>
      )}

      {groupedByMonth.map((monthGroup) => {
        const monthTitle = capitalizeFirstLetter(
          monthGroup.month.Moment.locale(translator.getCurrentLanguageId()).format('MMMM YYYY'),
        );

        return (
          <Box key={monthGroup.month.formatToShortDate()} flexDirection="column" gridGap="12px">
            <Typography component="h2" $fontSize="20px" $mb={2}>
              {monthTitle}
            </Typography>

            {monthGroup.attachments.map((attachment, index, arr) => (
              <Fragment key={attachment.id}>
                <PolicyAttachmentCard attachment={attachment} showNotificationIcon={showNotificationIcon} />

                {index < arr.length - 1 && <Divider />}
              </Fragment>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
